'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter } from '@/i18n/routing';
import { RefreshCw, AlertCircle } from 'lucide-react';
import CruiseCard, { CruiseCardData } from '../CruiseCard';

interface FeaturedCruisesClientProps {
  initialCruises: CruiseCardData[];
  initialError?: string | null;
}

const Spinner = () => (
  <svg className="animate-spin h-12 w-12 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);

export default function FeaturedCruisesClient({ initialCruises, initialError }: FeaturedCruisesClientProps) {
  const t = useTranslations();
  const router = useRouter();
  const [loading, setLoading] = useState(false);

  const handleRefresh = () => {
    setLoading(true);
    router.refresh(); // Re-fetch server components
    // We can't easily know when router.refresh() finishes without a transition,
    // but typically we can set a timeout or just let it replace the content.
    // For better UX, we might assume it finishes quickly or just show loading state briefly.
    setTimeout(() => setLoading(false), 2000);
  };

  return (
    <section className="w-full py-12 px-4">
      <div className="max-w-7xl mx-auto bg-white/85 dark:bg-slate-900/85 backdrop-blur-md rounded-3xl border border-slate-200 dark:border-slate-800 shadow-xl shadow-blue-900/5 dark:shadow-none p-8 md:p-12">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-slate-100 mb-3">
            {t('homePage.featuredCruises')}
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            {t('homePage.curatedLabel')}
          </p>
        </div>

        {/* Featured Cruise Cards - Grid Layout */}
        {!loading && !initialError && initialCruises.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {initialCruises.map((cruise, index) => (
              <div key={cruise.id ? `${cruise.id}-${index}` : cruise.main_cruise_id ? `${cruise.main_cruise_id}-${index}` : `cruise-${index}`} className="h-full">
                <CruiseCard
                  cruise={cruise}
                  variant="detailed"
                  hover={true}
                />
              </div>
            ))}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center min-h-[200px] py-8">
            <Spinner />
          </div>
        )}

        {/* Error State */}
        {initialError && (
          <div className="w-full">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center justify-between text-red-700 dark:text-red-300">
              <div className="flex items-center gap-2">
                <AlertCircle size={20} />
                <span>{initialError}</span>
              </div>
              <button
                onClick={handleRefresh}
                className="p-1 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-full transition-colors"
              >
                <RefreshCw size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && !initialError && initialCruises.length === 0 && (
          <div className="text-center py-8 text-slate-500 dark:text-slate-400">
            <p className="text-base">{t('homePage.noCruises')}</p>
          </div>
        )}
      </div>
    </section>
  );
}

